import { makeAutoObservable } from 'mobx'
import WheelOfLoveMetaService from '../../../shared/services/WheelOfLoveMeta.service'

const MODES = {
  PREPARING: 'preparing',
  PLAYING: 'playing',
  COMPLETED: 'completed',
}

const WINNER_AMOUNT = 100
const NON_WINNER_AMOUNT = 25
const META_KEY = 'contest-state'

// Extract array from various REST response shapes
const extractList = result => {
  if (!result) return []
  if (Array.isArray(result)) return result
  if (result.models && Array.isArray(result.models)) return result.models
  if (result.data && Array.isArray(result.data)) return result.data
  if (result.data && result.data.data && Array.isArray(result.data.data)) return result.data.data
  if (result.data && result.data.payload && Array.isArray(result.data.payload)) return result.data.payload
  return []
}

const getField = (record, field) => {
  if (typeof record.get === 'function') return record.get(field)
  return record[field]
}

class ContestStore {
  constructor() {
    makeAutoObservable(this)
  }

  mode = MODES.PREPARING
  fullscreen = false
  hideNamesList = true
  contestants = []
  winners = []
  spinCount = 0
  metaId = null
  contestWeekStart = null
  contestWeekEnd = null
  pendingWinner = null
  _saving = false
  isEnding = false

  get isPreparing() {
    return this.mode === MODES.PREPARING
  }

  get isPlaying() {
    return this.mode === MODES.PLAYING
  }

  get isCompleted() {
    return this.mode === MODES.COMPLETED
  }

  _snapshot = () => ({
    mode: this.mode,
    contestants: this.contestants.map(c => ({
      user_id: c.user_id,
      u_name: c.u_name,
      u_fname: c.u_fname,
      u_lname: c.u_lname,
      visible: c.visible,
      manuallyAdded: c.manuallyAdded,
      winnings: c.winnings,
      removedFromWheel: c.removedFromWheel || false,
      usertype_id: c.usertype_id,
    })),
    winners: this.winners.slice(),
    spinCount: this.spinCount,
  })

  setWeekDates = (weekStart, weekEnd) => {
    this.contestWeekStart = weekStart
    this.contestWeekEnd = weekEnd
  }

  // --- DB persistence ---

  _findExistingRow = async () => {
    if (!this.contestWeekStart) return null
    try {
      const result = await WheelOfLoveMetaService.search({
        search: {
          meta_key: META_KEY,
          contest_week_start: this.contestWeekStart,
        },
        pagination: false,
      })
      const list = extractList(result)
      if (list.length > 0) {
        const record = list[0]
        return {
          id: getField(record, 'id'),
          meta_value: getField(record, 'meta_value'),
        }
      }
    } catch (e) {
      console.error('[WOL] _findExistingRow error:', e)
    }
    return null
  }

  saveState = async () => {
    if (this._saving) return
    this._saving = true
    try {
      const snapshot = this._snapshot()
      const value = JSON.stringify(snapshot)

      // Always check for existing row
      if (!this.metaId) {
        const existing = await this._findExistingRow()
        if (existing) this.metaId = existing.id
      }

      if (this.metaId) {
        await WheelOfLoveMetaService.update(this.metaId, {
          meta_value: value,
        })
      } else {
        try {
          const result = await WheelOfLoveMetaService.store({
            meta_key: META_KEY,
            meta_value: value,
            contest_week_start: this.contestWeekStart,
            contest_week_end: this.contestWeekEnd,
          })
          // Try to extract the new id
          const id = result && result.id ? result.id : result && result.data ? result.data.id : null
          if (id) this.metaId = id

          // If we still don't have the id, fetch it
          if (!this.metaId) {
            const existing = await this._findExistingRow()
            if (existing) this.metaId = existing.id
          }
        } catch (insertErr) {
          // Duplicate key — fetch the existing row and update
          const existing = await this._findExistingRow()
          if (existing) {
            this.metaId = existing.id
            await WheelOfLoveMetaService.update(this.metaId, {
              meta_value: value,
            })
          }
        }
      }
    } catch (err) {
      console.error('[WOL] saveState error:', err)
    }
    this._saving = false
  }

  loadState = async weekStart => {
    try {
      const searchWeek = weekStart || this.contestWeekStart
      if (!searchWeek) return false

      // Temporarily set week for search
      const prevWeek = this.contestWeekStart
      this.contestWeekStart = searchWeek

      const existing = await this._findExistingRow()
      if (!existing) {
        this.contestWeekStart = prevWeek
        return false
      }

      this.metaId = existing.id
      const raw = existing.meta_value
      const saved = typeof raw === 'string' ? JSON.parse(raw) : raw

      if (saved && saved.contestants && saved.contestants.length > 0) {
        this.mode = saved.mode || MODES.PREPARING
        this.contestants = saved.contestants
        this.winners = saved.winners || []
        this.spinCount = saved.spinCount || 0
        this.pendingWinner = null

        if (this.mode === MODES.PLAYING) {
          document.body.classList.add('wol-playing')
        }
        return true
      }
    } catch (err) {
      console.error('[WOL] loadState error:', err)
    }
    return false
  }

  clearState = async () => {
    try {
      if (this.metaId) {
        await WheelOfLoveMetaService.delete(this.metaId)
        this.metaId = null
      }
    } catch (err) {
      console.error('[WOL] clearState error:', err)
    }
  }

  // --- Actions ---

  toggleFullscreen = () => {
    this.fullscreen = !this.fullscreen
    if (this.fullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen()
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    }
  }

  toggleHideNamesList = () => {
    this.hideNamesList = !this.hideNamesList
  }

  setMode = mode => {
    this.mode = mode
  }

  setEnding = value => {
    this.isEnding = value
  }

  startPlaying = () => {
    this.mode = MODES.PLAYING
    this.spinCount = 0
    this.winners = []
    this.pendingWinner = null
    document.body.classList.add('wol-playing')
    this.saveState()
  }

  endContest = () => {
    this.mode = MODES.COMPLETED
    document.body.classList.remove('wol-playing')
    this.clearState()
  }

  resetContest = () => {
    this.mode = MODES.PREPARING
    this.contestants = []
    this.winners = []
    this.spinCount = 0
    this.pendingWinner = null
    this.metaId = null
    document.body.classList.remove('wol-playing')
    this.clearState()
  }

  setContestants = agents => {
    this.contestants = agents.map(agent => ({
      ...agent,
      visible: true,
      manuallyAdded: false,
      winnings: 0,
      removedFromWheel: false,
    }))
  }

  toggleContestantVisibility = userId => {
    const contestant = this.contestants.find(c => c.user_id === userId)
    if (contestant) {
      contestant.visible = !contestant.visible
      this.saveState()
    }
  }

  addContestant = agent => {
    this.contestants.push({
      ...agent,
      visible: true,
      manuallyAdded: true,
      winnings: 0,
    })
    this.saveState()
  }

  removeContestant = userId => {
    this.contestants = this.contestants.filter(c => c.user_id !== userId)
    this.saveState()
  }

  get visibleContestants() {
    return this.contestants.filter(c => c.visible)
  }

  get totalSpins() {
    return Math.max(1, Math.floor(this.visibleContestants.length / 3))
  }

  get spinsRemaining() {
    return Math.max(0, this.totalSpins - this.spinCount)
  }

  get wheelCandidates() {
    return this.visibleContestants.filter(c => !c.removedFromWheel)
  }

  recordWin = userId => {
    const contestant = this.contestants.find(c => c.user_id === userId)
    if (contestant) {
      contestant.winnings = WINNER_AMOUNT
      const winnerData = {
        user_id: userId,
        name: contestant.u_name || `${contestant.u_fname} ${contestant.u_lname}`,
        amount: WINNER_AMOUNT,
        spinIndex: this.spinCount,
      }
      this.winners.push(winnerData)
      this.pendingWinner = winnerData
      this.spinCount++
      this.saveState()
    }
  }

  dismissWinner = () => {
    if (this.pendingWinner) {
      const contestant = this.contestants.find(c => c.user_id === this.pendingWinner.user_id)
      if (contestant) {
        contestant.removedFromWheel = true
      }
    }
    this.pendingWinner = null
    this.saveState()
  }
}

export { MODES, WINNER_AMOUNT, NON_WINNER_AMOUNT }
export default new ContestStore()
